/**
 * Constants for the company detail module.
 *
 * Contains base URLs and shared chart configuration.
 */

import type Highcharts from "highcharts";

// =============================================================================
// URLs
// =============================================================================

/**
 * Base URL for Sapphire external data.
 */
export const SAPPHIRE_BASE_URL = "https://sapphire.envisageworldwide.com";

// =============================================================================
// Chart Configuration
// =============================================================================

/**
 * Common exporting options for all Highcharts stock charts.
 */
export const CHART_EXPORTING_CONFIG: Highcharts.ExportingOptions = {
  enabled: true,
  buttons: {
    contextButton: {
      verticalAlign: "top",
      align: "right",
      x: 5,
      y: -10,
      symbolSize: 18,
      menuItems: [
        "viewFullscreen",
        "printChart",
        "downloadPNG",
        "downloadJPEG",
        "downloadPDF",
        "downloadSVG",
      ],
    },
  },
};

/**
 * Common range selector buttons for time-based charts.
 * Note: The "All" button with its click handler is added dynamically.
 */
export const RANGE_SELECTOR_BUTTONS: Highcharts.RangeSelectorButtonsOptions[] = [
  { type: "month", count: 1, text: "1m" },
  { type: "month", count: 3, text: "3m" },
  { type: "month", count: 6, text: "6m" },
  { type: "ytd", text: "YTD" },
  { type: "year", count: 1, text: "1y" },
  { type: "year", count: 3, text: "3y" },
  { type: "year", count: 5, text: "5y" },
];

/**
 * Extended range selector buttons including 10-year option.
 * Used for price performance charts.
 */
export const EXTENDED_RANGE_SELECTOR_BUTTONS: Highcharts.RangeSelectorButtonsOptions[] = [
  ...RANGE_SELECTOR_BUTTONS,
  { type: "year", count: 10, text: "10y" },
];

/**
 * Default line width for chart series.
 */
export const DEFAULT_LINE_WIDTH = 3;

/**
 * Default number of decimal places for value display.
 */
export const DEFAULT_VALUE_DECIMALS = 2;

// =============================================================================
// DOM Selectors
// =============================================================================

/**
 * DOM element IDs used by the company detail page.
 */
export const DOM_IDS = {
  mainTabs: "main-tabs",
  mainTabsMobile: "main-tabs-mobile",
  companySidebar: "company-sidebar",
  sidebarResearchGroup: "sidebar-research-group",
  companyDetailsTabs: "companyDetailsTabs",
  priceChart: "price-chart",
  peChart: "pe-chart",
  pbvChart: "pbv-chart",
  evEbitdaChart: "ev-ebitda-chart",
  evSalesChart: "ev-sales-chart",
  // Overlay chart containers
  priceChartOverlay: "price-chart-overlay",
  peChartOverlay: "pe-chart-overlay",
  pbvChartOverlay: "pbv-chart-overlay",
  evEbitdaChartOverlay: "ev-ebitda-chart-overlay",
  evSalesChartOverlay: "ev-sales-chart-overlay",
  // Page initialization
  companyData: "company-data",
} as const;

/**
 * Data attribute names used for page configuration.
 */
export const DATA_ATTRS = {
  companyTicker: "companyTicker",
  tradingItemId: "tradingItemId",
  exchangeCode: "exchangeCode",
  ciqCompanyId: "ciqCompanyId",
  currencyCode: "currencyCode",
  hasTeam: "hasTeam",
  hasCompanyEmbeds: "hasCompanyEmbeds",
  // Status action buttons
  statusAction: "statusAction",
  confirmMessage: "confirmMessage",
} as const;
